/* Common Imports */

import { styled, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import {
	fetchChatMessagesThunk,
	setChatMessagesFilterPageNumberThunk,
	setChatMessagesInitialLoadDoneThunk,
	setChatMessagesLoadingThunk,
	setChatMessagesTriggerNewSearchThunk,
} from "../../../../redux-magic/store";
import { ChatMessageInterface } from "../../../../redux-magic/sub-interfaces/chat-message-interface";

/* Component Imports */

import { CircularProgress, Typography } from "@mui/material";
import { useInView } from "react-intersection-observer";
import PropertyCardBroadcast from "../../../cards/PropertyCardBroadcast/PropertyCardBroadcastFetcher";

/* Icon Imports */

import { Done, DoneAll } from "@mui/icons-material";
import ImageStack from "./ImageStack";
import ImageStackUploadPreview from "./ImageStackUploadPreview";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column-reverse",
	justifyContent: "flex-start",
	width: "100%",
	height: "100%",
	overflowY: "scroll",
	overflowX: "hidden",
	gap: "0.75rem",
	background: theme.palette.background.default,
	padding: "0.75rem 1.25rem 1rem 1.25rem",
	position: "relative",
	overscrollBehavior: "contain",
	isolation: "isolate",
	"&::-webkit-scrollbar": {
		width: "6px",
		background: "transparent",
	},
	"&::-webkit-scrollbar-thumb": {
		background: "rgba(0, 0, 0, 0.2)",
		borderRadius: "3px",
		"&:hover": {
			background: "rgba(0, 0, 0, 0.3)",
		},
	},
	"&": {
		scrollbarWidth: "thin",
		scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
	},
	"&:not(:hover)::-webkit-scrollbar-thumb": {
		background: "transparent",
	},
	[theme.breakpoints.up("md1190")]: {
		// padding: "1.5rem 1.25rem 1rem 1.25rem",
	},
}));

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-end",
	width: "100%",
	gap: "0.5rem",
}));

const StyledDoneIcon = styled(Done)(({ theme }) => ({
	fontSize: "1rem",
	marginLeft: "0.25rem",
	color: theme.palette.text.secondary,
}));

const StyledDoneAllIcon = styled(DoneAll)(({ theme }) => ({
	fontSize: "1rem",
	marginLeft: "0.25rem",
	// color: ({ isRead }: { isRead: boolean }) => (isRead ? theme.palette.primary.main : theme.palette.text.secondary),
}));

const SystemMessage = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	gap: "0.5rem",
	width: "100%",
	marginTop: "1rem",
	marginBottom: "0.5rem",
}));

const ReceivedMessage = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-end",
	alignItems: "flex-start",
	gap: "0.5rem",
	width: "100%",
}));

const SentMessage = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-end",
	alignItems: "flex-end",
	gap: "0.5rem",
	width: "100%",
}));

const ChatBubbleWrapper = styled("div")<{ isSent: boolean }>(({ theme, isSent }) => ({
	position: "relative",
	width: "fit-content",
	maxWidth: "95%",
	borderRadius: isSent ? "1rem 0rem 1rem 1rem" : "0rem 1rem 1rem 1rem",
	alignSelf: isSent ? "flex-end" : "flex-start",
	background: isSent ? "#feeada" : "#ffffff",
}));

const ChatBubble = styled("div")<{ isSent: boolean }>(({ theme, isSent }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-end",
	borderRadius: "1rem",
	gap: "0.5rem",
	padding: "1rem 1rem 1rem 1rem",
	position: "relative",
	background: isSent ? "#feeada" : "#ffffff",
	"&::before": {
		content: '""',
		position: "absolute",
		width: 0,
		height: 0,
		top: "0px",
		border: 0,
		...(isSent
			? {
					right: "-8px",
					borderTop: "8px solid transparent",
					borderLeft: "8px solid " + (isSent ? "#feeada" : "#ffffff"),
					borderBottom: "8px solid transparent",
					// transform: "rotate(90deg)",
				}
			: {
					left: "-8px",
					borderTop: "8px solid transparent",
					borderRight: "8px solid " + (isSent ? "#feeada" : "#ffffff"),
					borderBottom: "8px solid transparent",
					// transform: "rotate(-90deg)",
				}),
		// filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
	},
}));

const ChatBubbleSystem = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	borderRadius: "1rem",
	padding: "0.5rem 1rem",
	background: theme.palette.mode === "dark" ? "rgba(145, 158, 171, 0.12)" : "rgba(99, 115, 129, 0.08)",
}));

const MessageBody = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	lineHeight: "1.25rem",
	fontWeight: "400",
	width: "fit-content",
	whiteSpace: "pre-wrap",
	wordBreak: "break-word",
	"& a": {
		color: theme.palette.primary.main,
		textDecoration: "underline",
		"&:hover": {
			textDecoration: "none",
		},
	},
}));

const Time = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	lineHeight: "0.875rem",
	fontWeight: "400",
	textAlign: "right",
	whiteSpace: "nowrap",
	color: theme.palette.text.secondary,
}));

const LoadMoreDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "7.5rem",
	pointerEvents: "none",
	cursor: "none",
	background: "transparent",
}));

const MessageSection = ({
	session,
	dispatch,
	router,
	profile_context,
	chat_id,
	messages,
	messages_count,
	messages_loading,
	initial_load_done,
	filter_page_number,
	selected_files,
	pending_uploads,
	current_message,
	setCurrentMessage,
	sendFiles,
	setSendFiles,
	setSelectedFilePreview,
	setPendingUploads,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	chat_id: string;
	messages: Array<ChatMessageInterface>;
	messages_count: number;
	messages_loading: boolean;
	initial_load_done: boolean;
	filter_page_number: number;
	selected_files: Array<string>;
	pending_uploads: Array<{ file: File; mime_type: string }>;
	current_message: string;
	setCurrentMessage: React.Dispatch<React.SetStateAction<string>>;
	sendFiles: boolean;
	setSendFiles: React.Dispatch<React.SetStateAction<boolean>>;
	setSelectedFilePreview: React.Dispatch<React.SetStateAction<Array<string>>>;
	setPendingUploads: React.Dispatch<React.SetStateAction<Array<{ file: File; mime_type: string }>>>;
}) => {
	const theme = useTheme();

	/* Fetch messages on initial load */

	React.useEffect(() => {
		if (!initial_load_done && session) {
			dispatch(setChatMessagesTriggerNewSearchThunk(true));
			dispatch(setChatMessagesInitialLoadDoneThunk(true));
			dispatch(setChatMessagesLoadingThunk(true));
			dispatch(
				fetchChatMessagesThunk({
					chat_id: chat_id,
					is_business_profile: profile_context.is_business_profile,
					user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					business_profile_id: profile_context.business_profile_id,
					page: 1,
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
				}),
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session]);

	/* Load more messages on scroll (Infinite scroll) */

	const { ref, inView } = useInView({ threshold: 0 });

	React.useEffect(() => {
		if (inView && initial_load_done && session) {
			dispatch(setChatMessagesFilterPageNumberThunk(filter_page_number + 1));
			dispatch(setChatMessagesLoadingThunk(true));
			dispatch(
				fetchChatMessagesThunk({
					chat_id: chat_id,
					is_business_profile: profile_context.is_business_profile,
					user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					business_profile_id: profile_context.business_profile_id,
					page: filter_page_number ? filter_page_number + 1 : 1,
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
				}),
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inView]);

	// Add this helper function at the top of the file with other imports
	const convertLinksToElements = (text: string) => {
		const urlPattern = /((https?:\/\/)?[\w-]+(\.[\w-]+)+\.?(:\d+)?(\/\S*)?)/gi;
		const emailPattern =
			/([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|mil|info|io|ai|ae|in|uk|us|ca|au|de|fr|jp|cn|ru|br|it|nl|es|se|no|fi|dk|ch|at|be|ie|nz))\b/gi;

		return text.split(/(\s+)/).map((part, index) => {
			if (!part.trim()) return part; // Return spaces and newlines as-is

			if (part.match(emailPattern)) {
				return (
					<a
						key={index}
						href={`mailto:${part}`}
						style={{
							color: "#1976d2",
							textDecoration: "underline",
							wordBreak: "break-all",
						}}
					>
						{part}
					</a>
				);
			}

			if (part.match(urlPattern)) {
				const url = part.startsWith("http") ? part : `https://${part}`;
				return (
					<a
						key={index}
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						style={{
							color: "#1976d2",
							textDecoration: "underline",
							wordBreak: "break-all",
						}}
					>
						{part}
					</a>
				);
			}

			return part;
		});
	};

	/* Function to handle the click event on a message */

	const getMessageDate = (date: Date): string => {
		const today = new Date();
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);

		if (date.toDateString() === today.toDateString()) {
			return "Today";
		} else if (date.toDateString() === yesterday.toDateString()) {
			return "Yesterday";
		} else {
			return date.toLocaleDateString("en-US", {
				year: "numeric",
				month: "long",
				day: "numeric",
			});
		}
	};

	return (
		<RootDiv>
			<SentMessage>
				{pending_uploads && pending_uploads.length > 0 && selected_files && selected_files.length > 0 && sendFiles && (
					<ImageStackUploadPreview
						session={session}
						dispatch={dispatch}
						profile_context={profile_context}
						router={router}
						chat_id={chat_id}
						images={selected_files}
						pending_uploads={pending_uploads}
						initial_load_done={initial_load_done}
						messages_loading={messages_loading}
						current_message={current_message}
						setCurrentMessage={setCurrentMessage}
						sendFiles={sendFiles}
						setSendFiles={(value) => {
							setSendFiles(value);
							if (!value) {
								setSelectedFilePreview([]);
								setPendingUploads([]);
							}
						}}
						setSelectedFilePreview={setSelectedFilePreview}
						setPendingUploads={setPendingUploads}
					/>
				)}
			</SentMessage>

			{messages.reduce((acc: JSX.Element[], message: any, index: number, array) => {
				const messageDate = new Date(message.created_at);
				const currentDateString = messageDate.toDateString();

				// Check date with next message since we're displaying in reverse
				const nextMessage = array[index + 1];
				const nextDateString = nextMessage ? new Date(nextMessage.created_at).toDateString() : null;

				acc.push(
					message.system_message === true ? (
						/* System Message */

						<SystemMessage key={index}>
							<ChatBubbleSystem>
								<Time variant="body2">
									{new Date(message.created_at).toLocaleTimeString("en-US", {
										hour: "2-digit",
										minute: "2-digit",
										hour12: true,
									})}
								</Time>
							</ChatBubbleSystem>
							<ChatBubbleSystem>
								<MessageBody>{convertLinksToElements(message.body)}</MessageBody>
							</ChatBubbleSystem>
						</SystemMessage>
					) : (message.sender.is_business_profile === true &&
							message.sender.business_profile_id === profile_context.business_profile_id) ||
					  (message.sender.is_business_profile === false && message.sender.user_id === profile_context.user_id) ? (
						/* Sent Message */

						<SentMessage key={index}>
							<ChatBubbleWrapper
								isSent={true}
								sx={{
									minWidth: message.is_broadcast ? "95%" : "fit-content",
								}}
							>
								<ChatBubble isSent={true}>
									<ColumnContainer>
										{message.is_broadcast ? (
											<PropertyCardBroadcast
												session={session}
												dispatch={dispatch}
												profile_context={profile_context}
												router={router}
												broadcast_id={message.broadcast_id}
												source="chat_message"
											/>
										) : null}
										{message && message.attachments.length > 0 && (
											<ImageStack images={message.attachments.map((att: any) => att.file_id)} />
										)}
										<RowContainer>
											<MessageBody variant="body2">{convertLinksToElements(message.body)}</MessageBody>
											<Time variant="body2">
												{new Date(message.created_at).toLocaleTimeString("en-US", {
													hour: "2-digit",
													minute: "2-digit",
													hour12: true,
												})}
											</Time>
											{/* {message.read_by?.some(
												(r: any) =>
													r.is_business_profile !== message.sender.is_business_profile ||
													(r.is_business_profile
														? r.business_profile_id.toString() !== message.sender.business_profile_id.toString()
														: r.user_id.toString() !== message.sender.user_id.toString()),
											) ? (
												<StyledDoneAllIcon
													sx={{
														color: theme.palette.primary.main,
													}}
												/>
											) : message.delivered_to?.some((r: any) => r.is_delivered) ? (
												<StyledDoneAllIcon
													sx={{
														color: theme.palette.text.secondary,
													}}
												/>
											) : (
												<StyledDoneIcon />
											)} */}
										</RowContainer>
									</ColumnContainer>
								</ChatBubble>
							</ChatBubbleWrapper>
						</SentMessage>
					) : (
						/* Received Message */

						<ReceivedMessage key={index}>
							<ChatBubbleWrapper
								isSent={false}
								sx={{
									minWidth: message.is_broadcast ? "95%" : "fit-content",
								}}
							>
								<ChatBubble isSent={false}>
									<ColumnContainer>
										{message.is_broadcast ? (
											<PropertyCardBroadcast
												session={session}
												dispatch={dispatch}
												profile_context={profile_context}
												router={router}
												broadcast_id={message.broadcast_id}
												source="chat_message"
											/>
										) : null}
										{message && message.attachments.length > 0 && (
											<ImageStack images={message.attachments.map((att: any) => att.file_id)} />
										)}
										<RowContainer>
											<MessageBody variant="body2">{convertLinksToElements(message.body)}</MessageBody>
											<Time variant="body2">
												{new Date(message.created_at).toLocaleTimeString("en-US", {
													hour: "2-digit",
													minute: "2-digit",
													hour12: true,
												})}
											</Time>
										</RowContainer>
									</ColumnContainer>
								</ChatBubble>
							</ChatBubbleWrapper>
						</ReceivedMessage>
					),
				);

				// Add date header when date changes or at the end of a group
				if (currentDateString !== nextDateString) {
					acc.push(
						<SystemMessage key={`date-${messageDate.getTime()}`}>
							<ChatBubbleSystem>
								<MessageBody
									sx={{
										fontSize: "0.75rem",
										color: "#637381",
										textAlign: "center",
										width: "100%",
										fontWeight: "500",
									}}
								>
									{getMessageDate(messageDate)}
								</MessageBody>
							</ChatBubbleSystem>
						</SystemMessage>,
					);
				}

				return acc;
			}, [])}

			{/* Load more div for infinite scroll */}

			{messages_count > messages.length && (
				<LoadMoreDiv ref={ref}>
					<CircularProgress />
				</LoadMoreDiv>
			)}
		</RootDiv>
	);
};

export default MessageSection;
